import { useMemo, useCallback, useEffect, useId, useState } from 'react';
import {
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Input,
  Menu,
  Panel,
  Skeleton,
} from '../../components/ui';
import { Icon } from '../../components/ui/Icon';
import { useCommentCounts } from '../comments/hooks';
import { useFileCounts } from '../files/hooks';
import {
  useCreateSection,
  useRenameSection,
  useReorderSections,
  useSetSectionArchived,
} from '../sections/hooks';
import { sectionErrorMessage } from '../sections/queries';
import { nextSectionPosition, swapPositions, visibleSections } from '../sections/types';
import {
  TASKS_FALLBACK_POLL_MS,
  useCreateTask,
  useDeleteTask,
  useMoveTaskStatus,
  useProjectTasks,
  useReorderTasks,
  useSetTaskCompleted,
  useTaskDefaults,
  useTasksRealtime,
  useUpdateTask,
} from '../tasks/hooks';
import { dueDateProblem } from '../tasks/taskLens';
import { taskErrorMessage } from '../tasks/queries';
import {
  nextTaskPosition,
  subtaskCounts,
  swapTaskPositions,
  TASK_PRIORITY_LABELS,
  tasksInSection,
} from '../tasks/types';
import { BoardView } from '../board/BoardView';
import { SectionFilterTabs } from '../board/SectionFilterTabs';
import { SectionPipelinePanel } from '../board/SectionPipelinePanel';
import { SwimlaneBoard } from '../board/SwimlaneBoard';
import { chipsByTask } from '../fields/fieldChips';
import { FieldManager } from '../fields/FieldManager';
import { useProjectFieldValues, useProjectFields } from '../fields/queries';
import { columnsForSection, toSectionStatusMap } from '../board/sectionPipeline';
import { useSectionStatuses } from '../projects/hooks';
import type { ProjectVisibility } from '../projects/types';
import type { BoardGrouping } from '../board/boardLens';
import { subtaskProgress } from '../board/boardLens';
import { SavedViewsBar } from '../savedViews/SavedViewsBar';
import { FilterToolbar } from './FilterToolbar';
import { applyTaskFilters, applyTaskSort, hasActiveFilters } from './taskFilters';
import { TaskDetailsPanel } from './TaskDetailsPanel';
import { TASK_DRAG_MIME, TaskTable, formatDate, memberLabel } from './TaskTable';
import { planReorder } from '../tasks/reorder';
import type { MenuItem } from '../../components/ui';
import { useAddProjectMember, useTapPeople } from '../projects/hooks';
import type { ProjectMemberWithProfile, ProjectStatus } from '../projects/types';
import type { SavedViewFilters, SavedViewMode, SavedViewSort } from '../savedViews/types';
import type { Section } from '../sections/types';
import type { Task, TaskPriority, TaskUpdate } from '../tasks/types';
import type { MoveTarget, TaskLookups, TaskRowActions } from './TaskTable';

/**
 * List View: tasks grouped into collapsible sections, with the default
 * columns from handoff 17, inline task creation inside each group and — as of
 * Stage 9 — inline cell editing, completion and reopening, reordering within
 * a section, and moving tasks to another section from the row menu. Stage 10
 * adds the task details panel, opened from each row's Details button, which
 * holds description editing, subtasks and deletion.
 *
 * Phase 5 Stage 1 adds the Board: a List | Board toggle in the panel header
 * switches how the same loaded, filtered tasks render (handoff 18, locked
 * decision: one page, shared toolbar and saved views). The chosen mode is
 * part of a saved view's arrangement.
 */

interface ListViewProps {
  projectId: string;
  /** Names the Drive subfolder this project's uploads land in. */
  projectName: string;
  sections: Section[];
  members: ProjectMemberWithProfile[];
  statuses: ProjectStatus[];
  /** May create tasks: TAP Admin, project owner, or a project member. */
  canEdit: boolean;
  /** May manage sections: TAP Admin or project owner. */
  canManage: boolean;
  /**
   * TAP Admins may assign anyone: picking a non-member silently adds them
   * to the project first (membership is what grants access, so this is the
   * legitimate door). Off for personal spaces.
   */
  adminAssignEveryone?: boolean;
  deepLinkTaskId?: string | null;
  /** Comment to anchor to once the task drawer opens. */
  deepLinkCommentId?: string | null;
  /**
   * The visibility of the project these tasks belong to, which is what the
   * task panel's "Copy link" needs to say who can open the link it just put
   * on the clipboard.
   *
   * Optional, and absent is meaningful: a personal list has no project to
   * share and no one to share it with, so PersonalTasksPage passes nothing
   * and the control does not appear at all.
   */
  projectVisibility?: ProjectVisibility;
  /**
   * A private list: hides the columns that only make sense when other people
   * are involved (redesign spec: Personal Tasks is Todoist-light).
   */
  compact?: boolean;
  /**
   * How the Board is arranged.
   *
   * 'grouped' is what every project board has had: a Group by Status | Section
   * toggle, and completion untouched by the board.
   *
   * 'unified' is Personal Tasks. Columns are permanently statuses with a
   * Completed column that tracks the List View checkbox both ways, and
   * sections become filter tabs above the board instead of a second column
   * layout. Scoped rather than global on purpose: the two-way completion sync
   * reverses a decision the project boards were built on, and reversing it for
   * everybody is a separate call from fixing the personal list.
   */
  boardLayout?: 'grouped' | 'unified';
}

export interface QuickAdd {
  assigneeId: string | null;
  dueDate: string | null;
  priority: TaskPriority | null;
}

function AddTaskForm({
  onSubmit,
  pending,
  members,
}: {
  onSubmit: (title: string, quick: QuickAdd) => void;
  pending: boolean;
  members: ProjectMemberWithProfile[];
}) {
  const workspace = useTaskDefaults();
  const [title, setTitle] = useState('');
  const [touched, setTouched] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  // `undefined` means "nobody has touched this yet, use the workspace
  // default"; null means somebody deliberately cleared it. Seeding the state
  // directly would freeze whatever the default was on the render before the
  // settings query resolved, which is null.
  const [dueDate, setDueDate] = useState<string | null | undefined>(undefined);
  const [dateOpen, setDateOpen] = useState(false);
  const [priority, setPriority] = useState<TaskPriority | null>(null);
  const [dueProblem, setDueProblem] = useState('');

  const assignee = members.find((member) => member.user_id === assigneeId);
  const effectiveDue = dueDate === undefined ? workspace.defaultDueDate : dueDate;

  function submit() {
    const trimmed = title.trim();
    if (trimmed === '') {
      setTouched(true);
      return;
    }
    const problem = dueDateProblem(effectiveDue, workspace.requireDueDate);
    if (problem !== null) {
      // Said here rather than let through: the trigger refuses this write, and
      // 'task_due_date_required' arriving from Postgres explains nothing to
      // somebody who has just typed a task name.
      setDueProblem(problem);
      setDateOpen(true);
      return;
    }
    setTouched(false);
    setDueProblem('');
    onSubmit(trimmed, { assigneeId, dueDate: effectiveDue, priority });
    setTitle('');
    setAssigneeId(null);
    setDueDate(undefined);
    setDateOpen(false);
    setPriority(null);
  }

  return (
    <div className="task-add">
      <Input
        label="Task name"
        labelHidden
        placeholder="New task name…"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
        {...(touched ? { error: 'Enter a task name.' } : {})}
      />
      <Menu
        trigger={
          <span className="task-add-ghost" aria-hidden="true">
            @ {assignee ? memberLabel(assignee) : 'Assignee'}
          </span>
        }
        triggerLabel="Quick assign"
        items={[
          { label: 'No assignee', onSelect: () => setAssigneeId(null) },
          ...members.map((member) => ({
            label: memberLabel(member),
            onSelect: () => setAssigneeId(member.user_id),
          })),
        ]}
      />
      <span className="task-add-date">
        <button
          type="button"
          className="task-add-ghost"
          // The visible text is the date once one is seeded, which leaves the
          // control with no name of its own — a screen reader reads out
          // "Aug 25, 2026" and nothing about what it opens. Not "Due date":
          // that is the name of the input inside, and two things answering to
          // one name is how a query finds the wrong one.
          aria-label="Set due date"
          aria-expanded={dateOpen}
          onClick={() => setDateOpen((value) => !value)}
        >
          {/* The seeded date shows on the closed button on purpose. A default
              applied silently is a date somebody did not choose and cannot
              see; showing it makes it an offer rather than a surprise. */}
          <Icon name="calendar" size="sm" /> {effectiveDue ? formatDate(effectiveDue) : 'Due date'}
        </button>
        {dateOpen ? (
          <span className="task-add-pop">
            <Input
              label="Due date"
              labelHidden
              type="date"
              value={effectiveDue ?? ''}
              {...(dueProblem === '' ? {} : { error: dueProblem })}
              onChange={(event) => {
                setDueDate(event.target.value === '' ? null : event.target.value);
                setDueProblem('');
                setDateOpen(false);
              }}
            />
          </span>
        ) : null}
      </span>
      <Menu
        trigger={
          <span className="task-add-ghost" aria-hidden="true">
            <Icon name="flag" size="sm" />{' '}
            {priority === null ? 'Priority' : TASK_PRIORITY_LABELS[priority]}
          </span>
        }
        triggerLabel="Quick priority"
        items={[
          { label: 'No priority', onSelect: () => setPriority(null) },
          ...(['high', 'medium', 'low'] as TaskPriority[]).map((value) => ({
            label: TASK_PRIORITY_LABELS[value],
            onSelect: () => setPriority(value),
          })),
        ]}
      />
      <Button onClick={submit} disabled={pending}>
        {pending ? 'Adding…' : 'Add task'}
      </Button>
    </div>
  );
}

function SectionGroup({
  name,
  onDropTask,
  onDropBefore,
  tasks,
  lookups,
  counts,
  commentCounts,
  fileCounts,
  actions,
  onAddTask,
  addPending,
  canEdit,
  moveTargets,
  rowActions,
  compact = false,
}: {
  name: string;
  /** Null when this group cannot accept a drop, e.g. read-only. */
  onDropTask: ((taskId: string) => void) | null;
  /**
   * Precise drops, indexed into the OPEN task list. The completed drawer is
   * deliberately excluded: its rows are a separate table with their own
   * indices, and ordering finished work is not a thing anybody needs.
   */
  onDropBefore: ((taskId: string, index: number, openTasks: Task[]) => void) | null;
  tasks: Task[];
  lookups: TaskLookups;
  counts: Record<string, number>;
  commentCounts: Record<string, number>;
  fileCounts: Record<string, number>;
  actions: MenuItem[] | null;
  onAddTask: ((title: string, quick: QuickAdd) => void) | null;
  addPending: boolean;
  canEdit: boolean;
  moveTargets: MoveTarget[];
  rowActions: TaskRowActions;
  compact?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const bodyId = useId();
  const [dragOver, setDragOver] = useState(false);

  const openTasks = tasks.filter((task) => !task.is_completed);
  const doneTasks = tasks.filter((task) => task.is_completed);

  return (
    <article className="section-group">
      <header className="section-group-header">
        <button
          type="button"
          className="section-toggle"
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          onClick={() => setCollapsed((value) => !value)}
        >
          <span aria-hidden="true" className="section-toggle-caret">
            {collapsed ? '▸' : '▾'}
          </span>
          <span className="section-name">{name}</span>
          <span className="section-count">
            {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
          </span>
        </button>
        {actions ? (
          <Menu
            trigger={<span aria-hidden="true">•••</span>}
            triggerLabel={`Section actions for ${name}`}
            items={actions}
          />
        ) : null}
      </header>

      {collapsed ? null : (
        <div
          className={`section-body${dragOver ? ' section-body--drop' : ''}`}
          id={bodyId}
          onDragOver={
            onDropTask === null
              ? undefined
              : (event) => {
                  // Only claim the drop when the payload is one of ours;
                  // otherwise a file dragged over the page gets swallowed.
                  if (!event.dataTransfer.types.includes(TASK_DRAG_MIME)) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDragOver(true);
                }
          }
          onDragLeave={() => setDragOver(false)}
          onDrop={
            onDropTask === null
              ? undefined
              : (event) => {
                  event.preventDefault();
                  setDragOver(false);
                  const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
                  if (taskId !== '') onDropTask(taskId);
                }
          }
        >
          {openTasks.length === 0 && doneTasks.length === 0 ? (
            <p className="section-empty">No tasks yet</p>
          ) : null}
          {openTasks.length > 0 ? (
            <TaskTable
              tasks={openTasks}
              lookups={lookups}
              subtaskCounts={counts}
              commentCounts={commentCounts}
              fileCounts={fileCounts}
              caption={`Tasks in ${name}`}
              canEdit={canEdit}
              moveTargets={moveTargets}
              actions={
                onDropBefore === null
                  ? rowActions
                  : {
                      ...rowActions,
                      onDropBefore: (taskId, index) => onDropBefore(taskId, index, openTasks),
                    }
              }
              compact={compact}
            />
          ) : null}
          {doneTasks.length > 0 ? (
            <details className="task-completed-drawer">
              <summary>Completed ({doneTasks.length})</summary>
              <TaskTable
                tasks={doneTasks}
                lookups={lookups}
                subtaskCounts={counts}
                commentCounts={commentCounts}
                fileCounts={fileCounts}
                caption={`Completed tasks in ${name}`}
                canEdit={canEdit}
                moveTargets={moveTargets}
                actions={rowActions}
                compact={compact}
              />
            </details>
          ) : null}
          {canEdit && onAddTask ? (
            <AddTaskForm onSubmit={onAddTask} pending={addPending} members={lookups.members} />
          ) : null}
        </div>
      )}
    </article>
  );
}

export function ListView({
  projectId,
  projectName,
  sections,
  members,
  statuses,
  canEdit,
  canManage,
  adminAssignEveryone = false,
  deepLinkTaskId = null,
  deepLinkCommentId = null,
  projectVisibility,
  compact = false,
  boardLayout = 'grouped',
}: ListViewProps) {
  const ordered = visibleSections(sections);
  const unifiedBoard = boardLayout === 'unified';

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState<Section | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  /**
   * The task the row menu asked to delete. Held as the whole row, not an id:
   * the optimistic delete removes it from the loaded list the moment it is
   * confirmed, and a dialog that looked its title up by id would blank out
   * mid-animation.
   */
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);
  // Inbox deep links: the routed page consumes ?task= and hands the id down.
  useEffect(() => {
    if (deepLinkTaskId != null) {
      setSelectedTaskId(deepLinkTaskId);
    }
  }, [deepLinkTaskId]);
  const [filters, setFilters] = useState<SavedViewFilters>({});
  const [sort, setSort] = useState<SavedViewSort>({});
  const [viewMode, setViewMode] = useState<SavedViewMode>('list');
  /**
   * Board grouping. Deliberately NOT part of the saved view: a saved view
   * stores what you are looking at, and this is how you are looking at it.
   * Folding it in would have every shared view carry somebody else's habit.
   */
  const [boardGrouping, setBoardGrouping] = useState<BoardGrouping>('status');
  const [error, setError] = useState('');
  // Stable so the rename dialog's focus trap is not rebuilt on every render:
  // an inline arrow here re-runs the trap effect on each keystroke and pulls
  // focus out of the field after the first character.
  const closeRename = useCallback(() => setRenaming(null), []);
  const closeDetails = useCallback(() => setSelectedTaskId(null), []);
  // Stable so the confirm dialog's focus trap is not rebuilt on every render.
  const closeDelete = useCallback(() => setDeletingTask(null), []);
  /**
   * A saved view handing over its stored arrangement (Stage 7; view mode
   * since Phase 5). Null mode means keep the current one — the All-tasks
   * reset clears filters without yanking the person out of their view.
   */
  const applyArrangement = useCallback(
    (nextFilters: SavedViewFilters, nextSort: SavedViewSort, nextMode: SavedViewMode | null) => {
      setFilters(nextFilters);
      setSort(nextSort);
      if (nextMode !== null) {
        setViewMode(nextMode);
      }
    },
    [],
  );

  // One realtime channel on this project's tasks while the page is open;
  // whenever it is not live, the list falls back to a 30-second poll so
  // nothing depends on realtime being available (handoff 39).
  const sectionStatuses = useSectionStatuses(projectId);
  // Two calls for the whole board, not two per card. Empty on every project
  // whose template defines no fields, which is all of them but Social Media.
  const fieldDefinitions = useProjectFields(projectId);
  const fieldValues = useProjectFieldValues(projectId);
  const fieldChips = useMemo(
    () => chipsByTask(fieldDefinitions.data ?? [], fieldValues.data ?? []),
    [fieldDefinitions.data, fieldValues.data],
  );
  const realtime = useTasksRealtime(projectId);
  const projectTasks = useProjectTasks(
    projectId,
    realtime === 'live' ? undefined : TASKS_FALLBACK_POLL_MS,
  );
  const commentCounts = useCommentCounts(projectId);
  const fileCounts = useFileCounts(projectId);
  const createSection = useCreateSection(projectId);
  const renameSection = useRenameSection(projectId);
  const archiveSection = useSetSectionArchived(projectId);
  const reorderSections = useReorderSections(projectId);
  const createTask = useCreateTask(projectId);
  const updateTask = useUpdateTask(projectId);
  const setCompleted = useSetTaskCompleted(projectId);
  const reorderTasks = useReorderTasks(projectId);
  const deleteTask = useDeleteTask(projectId);
  const moveTaskStatus = useMoveTaskStatus(projectId);
  const tapPeople = useTapPeople(adminAssignEveryone);
  const addOutsider = useAddProjectMember(projectId);

  const tasks = projectTasks.data?.tasks ?? [];
  const counts = subtaskCounts(projectTasks.data?.subtasksByParent ?? {});
  const threadCounts = commentCounts.data ?? {};
  const attachmentCounts = fileCounts.data ?? {};
  // Admin reach: everyone else in TAP appears as an assignable person,
  // labelled so it is clear that choosing them adds them to the project.
  const memberUserIds = new Set(members.map((member) => member.user_id));
  const assignableMembers =
    adminAssignEveryone && tapPeople.data
      ? [
          ...members,
          ...tapPeople.data
            .filter((person) => !memberUserIds.has(person.id))
            .map((person) => ({
              id: `tap-person-${person.id}`,
              user_id: person.id,
              project_role: 'member' as const,
              display_name: `${
                person.display_name.trim() === '' ? person.email : person.display_name
              } (will be added to project)`,
              email: person.email,
              // A placeholder for somebody not yet on the project. Initials on
              // purpose: the picture appears once they are a member, and the
              // row reads as "will be added", not as a member row.
              avatar_url: null,
            })),
        ]
      : members;
  const lookups: TaskLookups = { members: assignableMembers, statuses };

  /** Runs `then` once the person is assignable, adding them if needed. */
  function ensureAssignable(userId: string | null, then: () => void) {
    if (userId === null || memberUserIds.has(userId) || !adminAssignEveryone) {
      then();
      return;
    }
    addOutsider.mutate({ userId, projectRole: 'member' }, { onSuccess: then, onError: reportTask });
  }

  // The toolbar is a lens over the loaded list: filters narrow which rows
  // show, sort rearranges them inside their section, and neither writes
  // anything. Mutations (add, reorder, move) keep using the UNFILTERED list
  // so a hidden neighbour still counts for positioning.
  const filtering = hasActiveFilters(filters);
  const visibleTasks = applyTaskFilters(tasks, filters, {
    commentCounts: threadCounts,
    subtaskCounts: counts,
  });

  /**
   * The columns the board draws for whatever section is selected.
   *
   * Computed from `tasks` rather than `visibleTasks` on purpose. The
   * keep-a-column-that-holds-tasks rule has to see everything in the section,
   * not just what survived the current search or assignee filter — otherwise
   * typing in the search box would make a column disappear along with its
   * tasks, which is the exact disappearance the rule exists to prevent.
   */
  const sectionStatusMap = toSectionStatusMap(sectionStatuses.data ?? []);

  /**
   * Whether the board's COLUMNS are statuses right now.
   *
   * The unified board's columns are always statuses. A grouped board's follow
   * the Group by toggle — and when it is set to Section, the columns ARE the
   * sections and `statuses` only feeds the card's move-to menu. Narrowing it
   * there would quietly remove valid move targets based on whichever section
   * the toolbar filter happened to hold.
   */
  const statusColumns = unifiedBoard || boardGrouping === 'status';

  /**
   * Narrowed to the columns the selected section draws — but only where that
   * means something.
   *
   * Shipped first gated on `unifiedBoard`, which is wrong: only Personal Tasks
   * passes boardLayout='unified', so the feature was invisible on the project
   * boards it was built for. The condition that matters is whether the columns
   * are statuses, not which layout is in use.
   */
  const boardStatuses = statusColumns
    ? columnsForSection(statuses, sectionStatusMap, tasks, filters.sectionId)
    : statuses;

  function sectionRows(sectionId: string | null): Task[] {
    return applyTaskSort(tasksInSection(visibleTasks, sectionId), sort);
  }

  function reportSection(cause: unknown) {
    setError(sectionErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function reportTask(cause: unknown) {
    setError(taskErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function submitNewSection() {
    const name = newName.trim();
    if (name === '') {
      setError('Enter a section name.');
      return;
    }
    setError('');
    createSection.mutate(
      { projectId, name, position: nextSectionPosition(sections) },
      {
        onSuccess: () => {
          setNewName('');
          setAdding(false);
        },
        onError: reportSection,
      },
    );
  }

  function submitRename() {
    if (!renaming) {
      return;
    }
    const name = renameValue.trim();
    if (name === '') {
      setError('Enter a section name.');
      return;
    }
    setError('');
    renameSection.mutate(
      { sectionId: renaming.id, name },
      { onSuccess: closeRename, onError: reportSection },
    );
  }

  function move(section: Section, direction: 'up' | 'down') {
    const updates = swapPositions(sections, section.id, direction);
    if (!updates) {
      return;
    }
    setError('');
    reorderSections.mutate(updates, { onError: reportSection });
  }

  function addTask(sectionId: string | null, title: string, quick: QuickAdd) {
    setError('');
    createTask.mutate(
      {
        projectId,
        title,
        sectionId,
        statusId: null,
        assigneeId: quick.assigneeId,
        priority: quick.priority,
        dueDate: quick.dueDate,
        position: nextTaskPosition(tasksInSection(tasks, sectionId)),
      },
      { onError: reportTask },
    );
  }

  function editTask(task: Task, changes: TaskUpdate) {
    setError('');
    const nextAssignee = 'assignee_id' in changes ? (changes.assignee_id ?? null) : null;
    ensureAssignable(nextAssignee, () =>
      updateTask.mutate({ taskId: task.id, changes }, { onError: reportTask }),
    );
  }

  /** Completion only: the checkbox never touches status, by locked decision. */
  function toggleComplete(task: Task, isCompleted: boolean) {
    setError('');
    setCompleted.mutate({ taskId: task.id, isCompleted }, { onError: reportTask });
  }

  function moveTask(task: Task, direction: 'up' | 'down') {
    const ordered = tasksInSection(tasks, task.section_id);
    const updates = swapTaskPositions(ordered, task.id, direction);
    if (!updates) {
      return;
    }
    setError('');
    reorderTasks.mutate(updates, { onError: reportTask });
  }

  /**
   * A list-row drop landing at a precise index, from the grip.
   *
   * One gesture can be two changes at once — a row dragged from another
   * section both moves section and takes a position in its new one — so both
   * are written together rather than as a move followed by a reorder, which
   * would flash the row at the end of the list first.
   *
   * The plan is computed against the DESTINATION section in display order.
   * When it comes back as a reindex the gap has run out, and every row in that
   * section is renumbered in one batch.
   */
  function dropTaskAtIndex(
    taskId: string,
    sectionId: string | null,
    index: number,
    destination: Task[],
  ) {
    const task = tasks.find((row) => row.id === taskId);
    if (!task) {
      return;
    }
    // Planned against exactly the list the index came from, so the row lands
    // where the person dropped it rather than where a differently-filtered
    // list would have put it.
    const plan = planReorder(destination, taskId, index);
    if (plan.kind === 'noop' && task.section_id === sectionId) {
      return;
    }
    setError('');

    const moved = plan.updates.find((update) => update.id === taskId);
    if (task.section_id !== sectionId) {
      // The section change carries the moving row's own position, so the row
      // never appears in the wrong place between the two writes.
      editTask(task, {
        section_id: sectionId,
        position: moved?.position ?? nextTaskPosition(destination),
      });
    }

    const others = plan.updates.filter((update) => update.id !== taskId);
    if (task.section_id === sectionId && moved) {
      reorderTasks.mutate([moved, ...others], { onError: reportTask });
    } else if (others.length > 0) {
      reorderTasks.mutate(others, { onError: reportTask });
    }
  }

  /**
   * A list-row drop into a section header group.
   *
   * Resolved from the UNFILTERED list, like the Board: a drop still has to
   * land when the toolbar is narrowing what is on screen. A drop into the
   * section the task is already in is a no-op, not a write.
   */
  function dropTaskIntoSection(taskId: string, sectionId: string | null) {
    const task = tasks.find((row) => row.id === taskId);
    if (!task || task.section_id === sectionId) {
      return;
    }
    setError('');
    moveTaskToSection(task, sectionId);
  }

  /**
   * A Board move: status only, resolved from the UNFILTERED list so a drop
   * still lands when the toolbar is narrowing the view. A drop on the column
   * the card is already in is a no-op, not a write.
   */
  function moveTaskToStatus(taskId: string, statusId: string | null) {
    const task = tasks.find((row) => row.id === taskId);
    if (!task || task.status_id === statusId) {
      return;
    }
    setError('');
    moveTaskStatus.mutate({ taskId, statusId }, { onError: reportTask });
  }

  /**
   * A Board move on the unified board, where a column means a status AND a
   * completion state.
   *
   * Both are written in one update rather than as two mutations, so the card
   * cannot be seen half-moved and a refusal rolls back the whole gesture. The
   * no-op guard checks BOTH fields: dragging a ticked task from Completed to
   * the status it already had is still a real change — it un-ticks it — and an
   * early return on status alone would have swallowed it.
   */
  function moveTaskToColumn(
    taskId: string,
    intent: { statusId: string | null; isCompleted: boolean },
  ) {
    const task = tasks.find((row) => row.id === taskId);
    if (!task) {
      return;
    }
    if (task.status_id === intent.statusId && task.is_completed === intent.isCompleted) {
      return;
    }
    setError('');
    moveTaskStatus.mutate(
      { taskId, statusId: intent.statusId, isCompleted: intent.isCompleted },
      { onError: reportTask },
    );
  }

  /**
   * A Board move in section mode. Resolved from the UNFILTERED list for the
   * same reason the status version is, and a drop on the column the card is
   * already in is a no-op rather than a write that only changes position.
   */
  function moveTaskToSectionById(taskId: string, sectionId: string | null) {
    const task = tasks.find((row) => row.id === taskId);
    if (!task || task.section_id === sectionId) {
      return;
    }
    setError('');
    moveTaskToSection(task, sectionId);
  }

  /**
   * A drop on the swimlane board writes BOTH fields.
   *
   * The lane is the section and the column is the status, so one gesture can
   * change either or both. Writing them as two mutations would put a moment on
   * screen where the card has moved sideways but not down — and if the second
   * failed it would stay there.
   *
   * Each half is skipped when it did not change, so dragging a card within its
   * own lane is one write rather than two.
   */
  function moveTaskToLane(taskId: string, sectionId: string | null, statusId: string | null) {
    const task = tasks.find((row) => row.id === taskId);
    if (!task) {
      return;
    }
    setError('');
    if (task.section_id !== sectionId) {
      moveTaskToSection(task, sectionId);
    }
    if (task.status_id !== statusId) {
      moveTaskToStatus(taskId, statusId);
    }
  }

  /** Renaming a column writes the section itself, not a board-local label. */
  function renameSectionInline(sectionId: string, name: string) {
    setError('');
    renameSection.mutate({ sectionId, name }, { onError: reportSection });
  }

  function createSectionInline(name: string) {
    setError('');
    createSection.mutate(
      { projectId, name, position: nextSectionPosition(sections) },
      { onError: reportSection },
    );
  }

  /** Moving a task lands it after the last task in the destination section. */
  function moveTaskToSection(task: Task, sectionId: string | null) {
    editTask(task, {
      section_id: sectionId,
      position: nextTaskPosition(tasksInSection(tasks, sectionId)),
    });
  }

  /**
   * Confirmed delete. The mutation is optimistic and takes subtasks with the
   * parent, so the row and any open details panel for it both disappear from
   * the loaded list without waiting for the round trip. On failure the hook
   * restores its snapshot and the message lands in the panel error line.
   */
  function confirmDelete() {
    const task = deletingTask;
    if (!task) {
      return;
    }
    setError('');
    deleteTask.mutate(task.id, {
      onSuccess: () => setDeletingTask(null),
      onError: (cause) => {
        setDeletingTask(null);
        reportTask(cause);
      },
    });
  }

  const rowActions: TaskRowActions = {
    onEdit: editTask,
    onToggleComplete: toggleComplete,
    onMove: moveTask,
    onMoveToSection: moveTaskToSection,
    onOpenDetails: (task) => setSelectedTaskId(task.id),
    onDelete: (task) => {
      setError('');
      setDeletingTask(task);
    },
  };

  // Resolved from the freshly loaded list, so a deleted task closes its own
  // panel and every saved edit is reflected immediately.
  const selectedTask = tasks.find((task) => task.id === selectedTaskId);

  function actionsFor(section: Section): MenuItem[] {
    return [
      {
        label: 'Rename',
        onSelect: () => {
          setRenameValue(section.name);
          setRenaming(section);
        },
      },
      { label: 'Move up', onSelect: () => move(section, 'up') },
      { label: 'Move down', onSelect: () => move(section, 'down') },
      {
        label: 'Archive section',
        danger: true,
        onSelect: () => {
          setError('');
          archiveSection.mutate(
            { sectionId: section.id, isArchived: true },
            { onError: reportSection },
          );
        },
      },
    ];
  }

  const headerActions = (
    <div className="tasks-panel-actions">
      <div className="view-toggle" role="group" aria-label="View mode">
        <button
          type="button"
          className="view-toggle-button"
          aria-pressed={viewMode === 'list'}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          type="button"
          className="view-toggle-button"
          aria-pressed={viewMode === 'board'}
          onClick={() => setViewMode('board')}
        >
          Board
        </button>
      </div>
      {/* Fields are a property of the project, not of one view, so this is
          offered on the board and the list alike. Managers only — defining a
          field changes what everybody sees, the same bar as renaming a
          section. RLS is the actual check; hiding the button is the courtesy. */}
      {canManage ? <FieldManager projectId={projectId} /> : null}
      {canManage && viewMode === 'list' ? (
        <Button variant="secondary" onClick={() => setAdding(true)}>
          Add section
        </Button>
      ) : null}
    </div>
  );

  // Tasks in no section at all. Hidden when empty (handoff 15).
  const looseTasks = sectionRows(null);

  function body() {
    if (projectTasks.isPending) {
      return <Skeleton lines={3} label="Loading tasks" />;
    }

    if (projectTasks.isError) {
      return (
        <EmptyState
          title="Tasks could not be loaded"
          description="Something went wrong. Try again in a moment."
        />
      );
    }

    const arrangementBar = (
      <>
        <SavedViewsBar
          projectId={projectId}
          filters={filters}
          sort={sort}
          viewMode={viewMode}
          onApply={applyArrangement}
        />
        <FilterToolbar
          filters={filters}
          sort={sort}
          lookups={lookups}
          sections={sections}
          matched={visibleTasks.filter((task) => task.parent_task_id === null).length}
          total={tasks.length}
          onFiltersChange={setFilters}
          onSortChange={setSort}
        />
        {filtering && visibleTasks.length === 0 ? (
          <EmptyState title="No tasks match these filters" />
        ) : null}
      </>
    );

    if (viewMode === 'board') {
      return (
        <div className="list-view">
          {arrangementBar}
          {/* Sections as tabs, not as a second column layout. The old Group by
              toggle made them columns, which meant choosing to see sections
              cost you the status columns and the Completed column with them.
              Grouped boards keep the toggle; it is only wrong where completion
              has to stay visible. */}
          {/* Offered only while ONE section is selected, and only to a manager.
              On All Tasks there is no single section whose columns this would
              be, and a control that quietly applied to something other than
              what is on screen is worse than no control. */}
          {statusColumns && canManage && typeof filters.sectionId === 'string' ? (
            <div className="section-pipeline-bar">
              <SectionPipelinePanel
                projectId={projectId}
                sectionId={filters.sectionId}
                sectionName={
                  sections.find((section) => section.id === filters.sectionId)?.name ??
                  'this section'
                }
                statuses={statuses}
                configured={sectionStatusMap}
              />
            </div>
          ) : null}
          {unifiedBoard ? (
            <SectionFilterTabs
              sections={sections}
              tasks={tasks}
              sectionId={filters.sectionId}
              onChange={(next) => {
                setFilters((current) => {
                  // The key is DELETED rather than set to undefined:
                  // hasActiveFilters counts keys, so a lingering
                  // `sectionId: undefined` would leave the toolbar insisting a
                  // filter was on with nothing visible to clear.
                  const rest = { ...current };
                  delete rest.sectionId;
                  return next === undefined ? rest : { ...rest, sectionId: next };
                });
              }}
            />
          ) : (
            <div className="board-grouping" role="group" aria-label="Group cards by">
              <span className="board-grouping-label">Group by</span>
              {/* Three, not two. Status alone puts one pipeline over the
                  whole project with every kind of work interleaved; Section
                  alone turns the sections into columns and loses the pipeline
                  entirely. Swimlanes is the row-and-column answer: which kind
                  of work down the side, how far along across. */}
              {(['status', 'section', 'swimlane'] as BoardGrouping[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  className="view-toggle-button"
                  aria-pressed={boardGrouping === option}
                  onClick={() => setBoardGrouping(option)}
                >
                  {option === 'status' ? 'Status' : option === 'section' ? 'Section' : 'Swimlanes'}
                </button>
              ))}
            </div>
          )}
          {boardGrouping === 'swimlane' && !unifiedBoard ? (
            <SwimlaneBoard
              tasks={visibleTasks}
              statuses={statuses}
              sections={sections}
              sectionStatuses={toSectionStatusMap(sectionStatuses.data ?? [])}
              sort={sort}
              members={members}
              progress={subtaskProgress(projectTasks.data?.subtasksByParent ?? {})}
              fieldChips={fieldChips}
              canEdit={canEdit}
              onOpenTask={(task) => setSelectedTaskId(task.id)}
              onMove={moveTaskToLane}
              onRenameTask={editTask}
              onFocusSection={(sectionId) => {
                // Dropping into one lane is the focused view: switch to the
                // status board and filter to that section, which is the
                // arrangement that already existed and already knows how to
                // draw one section's pipeline.
                setBoardGrouping('status');
                setFilters((current) => {
                  const rest = { ...current };
                  delete rest.sectionId;
                  return sectionId === null ? rest : { ...rest, sectionId };
                });
              }}
            />
          ) : (
            <BoardView
              tasks={visibleTasks}
              /* Narrowed to the columns this section draws. A shipping section
               showing "In Review" and "For Revision" spreads the columns that
               matter thin between ones that never fill.

               columnsForSection also keeps any column that still HOLDS tasks,
               configured or not — without that, narrowing a section would make
               its tasks vanish rather than move, and the person who narrowed
               it would have no way to know. */
              statuses={boardStatuses}
              sort={sort}
              members={members}
              progress={subtaskProgress(projectTasks.data?.subtasksByParent ?? {})}
              commentCounts={threadCounts}
              fileCounts={attachmentCounts}
              fieldChips={fieldChips}
              canEdit={canEdit}
              onOpenTask={(task) => setSelectedTaskId(task.id)}
              onMoveTask={moveTaskToStatus}
              grouping={boardGrouping}
              sections={sections}
              onMoveTaskToSection={moveTaskToSectionById}
              layout={boardLayout}
              onMoveTaskToColumn={moveTaskToColumn}
              onRenameTask={editTask}
              {...(canManage && !unifiedBoard
                ? {
                    onRenameSection: renameSectionInline,
                    onCreateSection: createSectionInline,
                  }
                : {})}
            />
          )}
        </div>
      );
    }

    return (
      <div className="list-view">
        {arrangementBar}
        {ordered
          .filter((section) => !filtering || sectionRows(section.id).length > 0)
          .map((section) => (
            <SectionGroup
              key={section.id}
              onDropTask={canEdit ? (taskId) => dropTaskIntoSection(taskId, section.id) : null}
              onDropBefore={
                canEdit
                  ? (taskId, index, openTasks) =>
                      dropTaskAtIndex(taskId, section.id, index, openTasks)
                  : null
              }
              name={section.name}
              tasks={sectionRows(section.id)}
              lookups={lookups}
              counts={counts}
              commentCounts={threadCounts}
              fileCounts={attachmentCounts}
              actions={canManage ? actionsFor(section) : null}
              onAddTask={(title, quick) => addTask(section.id, title, quick)}
              addPending={createTask.isPending}
              canEdit={canEdit}
              moveTargets={ordered
                .filter((other) => other.id !== section.id)
                .map((other) => ({ id: other.id, name: other.name }))}
              rowActions={rowActions}
              compact={compact}
            />
          ))}
        {looseTasks.length === 0 && ordered.length > 0 ? null : (
          <SectionGroup
            name={ordered.length === 0 ? 'Tasks' : 'Uncategorized'}
            // Dropping here CLEARS the section rather than setting one: this
            // group is the tasks that belong to none, and it has to be a way
            // back out of a section, not a one-way door.
            onDropTask={canEdit ? (taskId) => dropTaskIntoSection(taskId, null) : null}
            onDropBefore={
              canEdit
                ? (taskId, index, openTasks) => dropTaskAtIndex(taskId, null, index, openTasks)
                : null
            }
            tasks={looseTasks}
            lookups={lookups}
            counts={counts}
            commentCounts={threadCounts}
            fileCounts={attachmentCounts}
            actions={null}
            onAddTask={canEdit ? (title, quick) => addTask(null, title, quick) : null}
            addPending={createTask.isPending}
            canEdit={canEdit}
            moveTargets={ordered.map((other) => ({ id: other.id, name: other.name }))}
            rowActions={rowActions}
            compact={compact}
          />
        )}
      </div>
    );
  }

  return (
    <Panel title="Tasks" actions={headerActions}>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {adding ? (
        <div className="section-add">
          <Input
            label="Section name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <div className="section-add-actions">
            <Button onClick={submitNewSection} disabled={createSection.isPending}>
              {createSection.isPending ? 'Adding…' : 'Add'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setNewName('');
                setError('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {body()}

      {selectedTask ? (
        /* The spread on projectVisibility below is exactOptionalPropertyTypes:
           an optional prop may be absent or a value, never explicitly
           undefined, and a personal list passes nothing at all. */
        <TaskDetailsPanel
          /* Keyed like LeadPanel and CompanyPanel on TrackerPage, for the
             same reason: the description and the half-typed subtask are
             draft state initialised from the task the panel first rendered
             with. Opening a second task without closing the first only
             swaps the prop, so without this key the initialiser never runs
             again and task two shows task one's description — which then
             saves onto task two on the next keystroke. Reported 10 Sep. */
          key={selectedTask.id}
          task={selectedTask}
          subtasks={projectTasks.data?.subtasksByParent[selectedTask.id] ?? []}
          lookups={lookups}
          projectId={projectId}
          projectName={projectName}
          canEdit={canEdit}
          canManage={canManage}
          compact={compact}
          {...(projectVisibility === undefined ? {} : { projectVisibility })}
          highlightCommentId={deepLinkCommentId}
          ensureAssignable={ensureAssignable}
          onClose={closeDetails}
        />
      ) : null}

      <Dialog
        open={renaming !== null}
        title="Rename section"
        onClose={closeRename}
        footer={
          <>
            <Button variant="ghost" onClick={closeRename}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={renameSection.isPending}>
              {renameSection.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <Input
          label="Section name"
          value={renameValue}
          onChange={(event) => setRenameValue(event.target.value)}
        />
      </Dialog>

      <Dialog
        open={deletingTask !== null}
        title="Delete task?"
        onClose={closeDelete}
        footer={
          <>
            <Button variant="ghost" onClick={closeDelete}>
              Keep task
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleteTask.isPending}>
              {deleteTask.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
          </>
        }
      >
        <p>
          This deletes <strong>{deletingTask?.title ?? ''}</strong> and its subtasks. The activity
          history is kept.
        </p>
      </Dialog>
    </Panel>
  );
}
